import React, { useState, useEffect } from "react";
import PedidoRealizadoModal from '../Modal/ModalSeleccion.jsx';
import ModalConfirmacion from '../Modal/ModalConfirmacion.jsx';
import BotonAcceso from '../elementos/BotonAcceso';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from "axios";
import {
  UtensilsCrossed,
  Home,
  MapPin,
  Info,
  CheckCircle2,
  ChevronRight,
  Clock,
  Building2,
  AlertCircle
} from 'lucide-react';
import { useTheme } from "../guardarToken/themeContext";

const apiUrlMenuSemanals = process.env.REACT_APP_API_MENUSEMANALS;
const apiUrlPedidos = process.env.REACT_APP_API_PEDIDOS;
const apiUrlVistaPedidosProxima = process.env.REACT_APP_API_VISTAPEDIDOS_PROXIMA;
const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';
const apiUrlAuth = process.env.REACT_APP_API_AUTH || '/api/auth';

const diasDeLaSemana = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const Seleccion = () => {
  const { isDarkMode } = useTheme();
  const [platos, setPlatos] = useState([]);
  const [error, setError] = useState(null);
  const [selectedPlatos, setSelectedPlatos] = useState({});
  const [homeOffice, setHomeOffice] = useState({});
  // Para invitados/dirección: cantidad de viandas por día/plato
  const [cantidades, setCantidades] = useState({});
  const [hasExistingPedido, setHasExistingPedido] = useState(false);
  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
  const [showModalPedidoRealizado, setShowModalPedidoRealizado] = useState(false);
  const [observacion, setObservacion] = useState({});
  const [sedes, setSedes] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);
  const [perfilEntrega, setPerfilEntrega] = useState({
    ubicacion: "",
    sedeId: ""
  });
  const [perfilEntregaReady, setPerfilEntregaReady] = useState(false);
  const sedeUsuario = localStorage.getItem("sede");
  const navigate = useNavigate();
  const location = useLocation();

  const editMode = location.state?.editMode || false;
  const semanaBase = location.state?.semanaBase || null;
  const editarDia = location.state?.editarDia || null;
  const userSedeId = perfilEntrega.sedeId;
  const tipoUsuario = localStorage.getItem("tipoUsuario") || 'user';
  const permiteMultiplesMenus = tipoUsuario === 'invitado' || tipoUsuario === 'direccion';

  const [sedesPorDia, setSedesPorDia] = useState({});
  const [tiposPorSede, setTiposPorSede] = useState({}); // Tipos habilitados por cada sede

  useEffect(() => {
    const refreshPerfilEntrega = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const { data } = await axios.get(`${apiUrlAuth}/verify-token`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!data?.valid) return;

        const ubicacionActual = data.ubicacion || "";
        const sedeIdActual = data.sede_id ? String(data.sede_id) : "";

        setPerfilEntrega({
          ubicacion: ubicacionActual,
          sedeId: sedeIdActual
        });
      } catch (error) {
        console.error("Error al refrescar perfil de entrega:", error);
      } finally {
        setPerfilEntregaReady(true);
      }
    };

    refreshPerfilEntrega();
  }, []);

  useEffect(() => {
    const fetchSedes = async () => {
      try {
        if (!perfilEntregaReady) return;
        const authToken = localStorage.getItem("token");
        const [response, direccionesRes] = await Promise.all([
          axios.get('/api/sedes', {
            headers: { Authorization: `Bearer ${authToken}` }
          }),
          axios.get(apiUrlDirecciones, {
            headers: { Authorization: `Bearer ${authToken}` }
          })
        ]);
        const activeSedes = response.data.filter(s => s.activo);
        setSedes(activeSedes);
        setDirecciones(Array.isArray(direccionesRes.data) ? direccionesRes.data : []);

        // Cargar los tipos habilitados para cada sede
        const tiposMap = {};
        for (const sede of activeSedes) {
          try {
            const resTipos = await axios.get(`/api/sedes/${sede.id}/tipos`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            tiposMap[sede.id] = resTipos.data.map(t => t.nombre.toUpperCase().trim());
          } catch (err) {
            console.error(`Error cargando tipos para sede ${sede.id}:`, err);
            tiposMap[sede.id] = []; // Si falla, array vacío (mostrará todos)
          }
        }
        setTiposPorSede(tiposMap);

        // Establecer sede por defecto del usuario
        if (userSedeId && activeSedes.some(s => String(s.id) === String(userSedeId))) {
          setSedeSeleccionada(userSedeId);
        } else if (activeSedes.length > 0) {
          setSedeSeleccionada(activeSedes[0].id);
        }
      } catch (error) {
        console.error("Error al cargar sedes:", error);
      }
    };
    fetchSedes();
  }, [perfilEntregaReady, userSedeId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!perfilEntregaReady) return;
        // Obtenemos todos los platos de la semana para todas las sedes
        const responsePlatos = await axios.get(apiUrlMenuSemanals);
        let platosData = responsePlatos.data;

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const diaSemana = hoy.getDay();
        const diasHastaLunes = (8 - diaSemana) % 7 || 7;
        const lunesProximo = new Date(hoy);
        lunesProximo.setDate(hoy.getDate() + diasHastaLunes);
        lunesProximo.setHours(0, 0, 0, 0);
        const viernesProximo = new Date(lunesProximo);
        viernesProximo.setDate(lunesProximo.getDate() + 4);
        viernesProximo.setHours(23, 59, 59, 999);

        // Si estamos editando un solo día, filtrar solo ese día (sin restricción de días laborables)
        if (editMode && editarDia) {
          platosData = platosData.filter(p => p.fecha === editarDia);
        } else {
          // Solo aplicar filtro de días laborables si NO estamos editando un día específico
          platosData = platosData.filter(p => {
            const [ya, ym, yd] = String(p.fecha).split('-').map(Number);
            const f = new Date(ya, ym - 1, yd);
            const diaDeLaSemana = f.getDay(); // 0=Domingo, 6=Sábado
            // Excluir fines de semana: solo lunes (1) a viernes (5)
            return f >= lunesProximo && f <= viernesProximo && diaDeLaSemana >= 1 && diaDeLaSemana <= 5;
          });
        }

        if (editMode && semanaBase) {
          const base = new Date(semanaBase);
          base.setHours(0, 0, 0, 0);
          const end = new Date(base);
          end.setDate(base.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          platosData = platosData.filter(p => {
            const [ya, ym, yd] = String(p.fecha).split('-').map(Number);
            const f = new Date(ya, ym - 1, yd);
            const diaDeLaSemana = f.getDay();
            // Solo lunes a viernes
            return f >= base && f <= end && diaDeLaSemana >= 1 && diaDeLaSemana <= 5;
          });
        }
        setPlatos(platosData);

        // Inicializar sedesPorDia con el default del usuario
        const initialSedes = {};
        const fechasUnicas = [...new Set(platosData.map(p => p.fecha))];
        fechasUnicas.forEach(f => {
          if (userSedeId) initialSedes[f] = Number(userSedeId);
        });
        setSedesPorDia(initialSedes);
      } catch (error) {
        console.error("Error al obtener datos:", error);
        setError("Error al obtener los menús semanales.");
      }
    };
    fetchData();
  }, [editMode, perfilEntregaReady, semanaBase, editarDia, userSedeId]);

  useEffect(() => {
    const checkExistingPedido = async () => {
      try {
        const usuario = localStorage.getItem("nombreUsuario");
        const tipo = localStorage.getItem("tipoUsuario");

        const response = await axios.get(`${apiUrlPedidos}?usuario=${usuario}`);

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const diaSemana = hoy.getDay();

        // Calculamos el inicio de la próxima semana (que es el objetivo de la selección nueva)
        const diasHastaLunes = (8 - diaSemana) % 7 || 7;
        const lunesProximo = new Date(hoy);
        lunesProximo.setDate(hoy.getDate() + diasHastaLunes);
        lunesProximo.setHours(0, 0, 0, 0);

        if (tipo === 'invitado' || tipo === 'direccion') {
          setHasExistingPedido(false);
        } else {
          const pedidoExistente = response.data.some(pedido => {
            if (pedido.usuario !== usuario) return false;
            const [ya, ym, yd] = String(pedido.fecha).split('-').map(Number);
            const f = new Date(ya, ym - 1, yd);
            // Solo bloqueamos si el pedido existente corresponde a la semana objetivo o futuro
            return f >= lunesProximo;
          });
          setHasExistingPedido(editMode ? false : pedidoExistente);
          if (pedidoExistente && !editMode) {
            setShowModalPedidoRealizado(true);
          }
        }
      } catch (error) {
        console.error("Error al verificar el pedido existente:", error);
      }
    };
    checkExistingPedido();
  }, [editMode]);

  // Inicializar sedesPorDia con la sede del usuario si no estamos en editMode
  useEffect(() => {
    console.log("DEBUG: useEffect inicializar sedesPorDia");
    console.log("DEBUG: userSedeId:", userSedeId, typeof userSedeId);
    console.log("DEBUG: Platos length:", platos.length);

    if (!editMode && platos.length > 0 && userSedeId) {
      const dates = [...new Set(platos.map(p => p.fecha))];
      const initialSedes = {};
      dates.forEach(d => {
        initialSedes[d] = Number(userSedeId);
      });

      console.log("DEBUG: Setting initialSedes:", initialSedes);

      // Solo actualizamos si está vacío para no sobrescribir elecciones del usuario si cambia algo
      setSedesPorDia(prev => {
        console.log("DEBUG: Prev sedesPorDia:", prev);
        return (Object.keys(prev).length === 0 ? initialSedes : prev);
      });
    }
  }, [platos, userSedeId, editMode]);

  useEffect(() => {
    const preloadSeleccion = async () => {
      try {
        if (!editMode) return;
        const usuario = localStorage.getItem('nombreUsuario');
        const { data } = await axios.get(`${apiUrlVistaPedidosProxima}${usuario}`);

        // Si estamos editando un día específico, filtrar solo ese día
        const pedidosFiltrados = editarDia
          ? data.filter(p => p.fecha === editarDia)
          : data;

        const acc = {};
        const hoFlags = {};
        const dailySedes = {};
        const loadedCantidades = {};
        for (const p of pedidosFiltrados) {
          const fecha = p.fecha;
          const tipo = p.tipo;
          if (!acc[fecha]) acc[fecha] = {};
          if (!acc[fecha][tipo]) acc[fecha][tipo] = {};
          acc[fecha][tipo][p.id_plato] = {
            id_plato: p.id_plato,
            nombre: p.nombre,
            tipo: p.tipo,
          };
          hoFlags[fecha] = p.es_home_office || false;
          if (p.sede_id) dailySedes[fecha] = Number(p.sede_id);
          // Contar repeticiones de id_plato para restaurar cantidades (invitado/dirección)
          if (!loadedCantidades[fecha]) loadedCantidades[fecha] = {};
          if (!loadedCantidades[fecha][tipo]) loadedCantidades[fecha][tipo] = {};
          loadedCantidades[fecha][tipo][p.id_plato] = (loadedCantidades[fecha][tipo][p.id_plato] || 0) + 1;
        }
        setSelectedPlatos(acc);
        setHomeOffice(hoFlags);
        setSedesPorDia(dailySedes);
        setCantidades(loadedCantidades);
      } catch (e) {
        console.error('No se pudo precargar la selección para edición', e);
      }
    };
    preloadSeleccion();
  }, [editMode, editarDia]);

  // Para invitados/dirección: manejar cantidad
  const handleCantidadChange = (fecha, tipo, plato, value) => {
    const parsed = parseInt(value, 10);
    const cantidad = (!isNaN(parsed) && parsed >= 0) ? parsed : 0;
    setCantidades(prev => {
      const updated = { ...prev };
      if (!updated[fecha]) updated[fecha] = {};
      if (!updated[fecha][tipo]) updated[fecha][tipo] = {};
      updated[fecha][tipo][plato.id_plato || plato.id] = cantidad;
      return updated;
    });
    handleCheckboxChange(fecha, tipo, plato, true);
  };

  const handleCheckboxChange = (fecha, tipo, plato, forceChecked) => {
    if (hasExistingPedido) return;
    setSelectedPlatos(prevSelectedPlatos => {
      const updatedSelection = { ...prevSelectedPlatos };
      if (!updatedSelection[fecha]) updatedSelection[fecha] = {};

      if (!permiteMultiplesMenus) {
        Object.keys(updatedSelection[fecha]).forEach(selectedTipo => {
          if (selectedTipo !== tipo) {
            Object.keys(updatedSelection[fecha][selectedTipo]).forEach(platoId => {
              delete updatedSelection[fecha][selectedTipo][platoId];
            });
          }
        });
      }

      if (!updatedSelection[fecha][tipo]) updatedSelection[fecha][tipo] = {};
      const platoId = plato.id_plato || plato.id;
      const isSelected = !!updatedSelection[fecha][tipo][platoId];
      const checked = typeof forceChecked === 'boolean' ? forceChecked : !isSelected;
      if (checked) {
        updatedSelection[fecha][tipo][platoId] = plato;
      } else {
        delete updatedSelection[fecha][tipo][platoId];
      }
      if (Object.keys(updatedSelection[fecha][tipo]).length === 0) delete updatedSelection[fecha][tipo];
      if (Object.keys(updatedSelection[fecha]).length === 0) delete updatedSelection[fecha];
      return updatedSelection;
    });
    if (!observacion[fecha]) {
      setObservacion(prev => ({ ...prev, [fecha]: "" }));
    }
  };

  const enviarSeleccionABaseDeDatos = async () => {
    try {
      if (hasExistingPedido || !isAnyPlatoSelected()) return;
      const sector = localStorage.getItem("sector");
      const rawUbicacion = perfilEntrega.ubicacion || "";
      const ubicacionPerfil = (rawUbicacion && rawUbicacion !== "null" && rawUbicacion !== "undefined") ? rawUbicacion : "";
      const usuario = localStorage.getItem("nombreUsuario");
      const apellido = localStorage.getItem("apellido");
      const nombreUser = localStorage.getItem("nombre");
      const tipoUsuario = localStorage.getItem("tipoUsuario") || 'user';

      // Regla de negocio:
      // - Si no cambia la entrega, se respeta la dirección configurada en su perfil.
      // - Si cambia manualmente la sede de retiro, se usa la dirección de esa entrega.
      const getUbicacionParaFecha = (fecha) => {
        const sedeDia = sedesPorDia[fecha];
        if (!sedeDia || String(sedeDia) === String(userSedeId)) return ubicacionPerfil;
        const sedeObj = sedes.find(s => String(s.id) === String(sedeDia));
        if (sedeObj?.direccion) return sedeObj.direccion;
        const direccionDeSede = direcciones.find(d => String(d.sede_id) === String(sedeDia))?.direccion;
        return direccionDeSede || ubicacionPerfil;
      };

      const allDates = [...new Set([...Object.keys(selectedPlatos), ...Object.keys(homeOffice).filter(f => homeOffice[f])])];

      const seleccion = allDates.flatMap(fecha => {
        const platosEnFecha = selectedPlatos[fecha] || {};
        const tipos = Object.keys(platosEnFecha);
        const isHO = homeOffice[fecha] || false;

        if (isHO) {
          // Si es Home Office, enviamos al menos un registro representativo para que el backend cree la cabecera con flag HO
          return [{
            id_plato: null,
            nombre: "HOME OFFICE",
            tipo: "HO",
            fecha: fecha,
            usuario: usuario,
            nombreuser: nombreUser,
            apellido: apellido,
            sector: sector,
            ubicacion: "Home Office",
            observacion: observacion[fecha] || null,
            tipoUsuario: tipoUsuario,
            es_home_office: true,
            sede_id: userSedeId, // Para HO usamos la de base
          }];
        }

        return tipos.flatMap(tipo =>
          Object.keys(platosEnFecha[tipo]).flatMap(platoId => {
            const sel = platosEnFecha[tipo][platoId] || {};
            // Si es invitado/dirección, usar la cantidad
            if (permiteMultiplesMenus) {
              const cantidad = cantidades?.[fecha]?.[tipo]?.[platoId] || 1;
              return Array.from({ length: cantidad }).map(() => ({
                id_plato: sel.id_plato || sel.id || Number(platoId),
                nombre: sel.nombre,
                tipo: sel.tipo,
                fecha: fecha,
                usuario: usuario,
                nombreuser: nombreUser,
                apellido: apellido,
                sector: sector,
                ubicacion: getUbicacionParaFecha(fecha),
                observacion: observacion[fecha] || null,
                tipoUsuario: tipoUsuario,
                es_home_office: false,
                sede_id: sedesPorDia[fecha] || userSedeId,
              }));
            } else {
              return [{
                id_plato: sel.id_plato || sel.id || Number(platoId),
                nombre: sel.nombre,
                tipo: sel.tipo,
                fecha: fecha,
                usuario: usuario,
                nombreuser: nombreUser,
                apellido: apellido,
                sector: sector,
                ubicacion: getUbicacionParaFecha(fecha),
                observacion: observacion[fecha] || null,
                tipoUsuario: tipoUsuario,
                es_home_office: false,
                sede_id: sedesPorDia[fecha] || userSedeId,
              }];
            }
          })
        );
      });

      if (editMode && editarDia) {
        // Si estamos editando un día específico, primero eliminar los pedidos de ese día
        await axios.delete(`${apiUrlPedidos}by-date/${usuario}/${editarDia}`);
        // Luego crear los nuevos pedidos con flag de edición
        await axios.post(apiUrlPedidos, { nuevosPlatos: seleccion, isEditing: true });
      } else if (editMode) {
        // Editar toda la semana
        await axios.post(`${apiUrlPedidos}replace/${usuario}`, { nuevosPlatos: seleccion });
      } else {
        // Crear nuevo pedido
        await axios.post(apiUrlPedidos, { nuevosPlatos: seleccion });
      }

      setSelectedPlatos({});
      setShowModalConfirmacion(false);
      setObservacion({});
      navigate('/user/mipedidosemanal');
    } catch (error) {
      console.error("Error:", error);
      const mensaje = error.response?.data?.message || 'No se pudo enviar la selección.';
      setError(mensaje);
    }
  };

  const isPlatoSelected = (fecha, tipo, plato) => {
    const platoId = plato.id_plato || plato.id;
    return selectedPlatos[fecha]?.[tipo]?.[platoId];
  };

  const isAnyPlatoSelected = () => {
    return Object.keys(selectedPlatos).some(f => Object.keys(selectedPlatos[f]).some(t => Object.keys(selectedPlatos[f][t]).length > 0)) || Object.values(homeOffice).some(v => v);
  };

  // Organizar platos agrupados por fecha y tipo, eliminando duplicados por id_plato
  const platosOrganizados = {};
  platos.forEach(plato => {
    if (!platosOrganizados[plato.fecha]) platosOrganizados[plato.fecha] = {};
    if (!platosOrganizados[plato.fecha][plato.tipo]) platosOrganizados[plato.fecha][plato.tipo] = [];
    // Solo agregar si no existe ya un plato con ese id_plato y sede_id
    const existe = platosOrganizados[plato.fecha][plato.tipo].some(
      p => p.id_plato === plato.id_plato && p.sede_id === plato.sede_id
    );
    if (!existe) {
      platosOrganizados[plato.fecha][plato.tipo].push(plato);
    }
  });

  const tiposUnicos = [...new Set(platos.map(plato => plato.tipo))];

  // Inicializar cantidades para todos los platos visibles (solo invitados/dirección)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!permiteMultiplesMenus) return;
    const nuevasCantidades = { ...cantidades };
    Object.keys(platosOrganizados).forEach(fecha => {
      Object.keys(platosOrganizados[fecha]).forEach(tipo => {
        platosOrganizados[fecha][tipo].forEach(plato => {
          const platoId = plato.id_plato || plato.id;
          if (!nuevasCantidades[fecha]) nuevasCantidades[fecha] = {};
          if (!nuevasCantidades[fecha][tipo]) nuevasCantidades[fecha][tipo] = {};
          if (nuevasCantidades[fecha][tipo][platoId] === undefined || nuevasCantidades[fecha][tipo][platoId] === null) {
            nuevasCantidades[fecha][tipo][platoId] = 0;
          }
        });
      });
    });
    setCantidades(nuevasCantidades);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platos, permiteMultiplesMenus]);

  const parseFechaLocal = (fechaStr) => {
    const [a, m, d] = fechaStr.split("-");
    return new Date(Number(a), Number(m) - 1, Number(d));
  };

  const semanaTitulo = (() => {
    if (!editMode) return null;

    if (editarDia) {
      const [y, m, d] = editarDia.split('-').map(Number);
      const fecha = new Date(y, m - 1, d);
      const diaNombre = diasDeLaSemana[fecha.getDay()];
      const fmt = fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `Editando ${diaNombre} ${fmt}`;
    }

    if (semanaBase) {
      const base = new Date(semanaBase);
      const end = new Date(base);
      end.setDate(base.getDate() + 6);
      const fmt = (dt) => dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `Editando semana del ${fmt(base)} al ${fmt(end)}`;
    }

    return null;
  })();

  return (
    <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>
      {showModalPedidoRealizado && (
        <PedidoRealizadoModal
          show={showModalPedidoRealizado}
          handleClose={() => setShowModalPedidoRealizado(false)}
        />
      )}

      {/* HEADER SECTION */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 p-3 rounded-xl shadow-lg shadow-emerald-900/20">
            <UtensilsCrossed className="text-white w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight leading-none mb-1">Elección de Menú</h1>
            <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Selecciona tus viandas para la próxima semana</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <BotonAcceso
            variant="accent"
            onClick={() => { if (isAnyPlatoSelected()) setShowModalConfirmacion(true); }}
            disabled={hasExistingPedido || !isAnyPlatoSelected()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] uppercase tracking-widest px-6 py-2.5 rounded-xl shadow-lg shadow-emerald-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all disabled:opacity-50"
          >
            {editMode ? 'Guardar cambios' : 'Finalizar Pedido'} <ChevronRight className="w-4 h-4" />
          </BotonAcceso>
        </div>
      </div>

      {semanaTitulo && (
        <div className="mb-6 bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 rounded-xl px-5 py-3 text-xs font-black uppercase tracking-wider flex items-center gap-3 animate-in fade-in duration-500">
          <Info className="w-5 h-5" /> {semanaTitulo}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl px-5 py-3 text-xs font-black uppercase tracking-wider flex items-center gap-3 italic">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}

      {/* INFO DE SELECCIÓN */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800 shadow-xl" : "bg-white border-slate-200 shadow-sm"} rounded-2xl p-5 mb-8 border animate-in slide-in-from-left duration-700`}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-4 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="text-indigo-500 w-5 h-5" />
              <h2 className="text-sm font-black uppercase tracking-widest font-black">Información de Retiro</h2>
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Por defecto se asigna tu sede habitual, pero puedes cambiarla día por día en cada tarjeta si lo necesitas.
            </p>
          </div>
        </div>
      </div>

      {/* CARDS POR DÍA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 animate-in slide-in-from-bottom duration-1000">
        {Object.keys(platosOrganizados).sort().map((fecha, idx) => {
          const dateObj = parseFechaLocal(fecha);
          const isHO = homeOffice[fecha] || false;

          return (
            <div
              key={fecha}
              className={`relative overflow-hidden group rounded-3xl border transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/10 ${isDarkMode
                ? (isHO ? "bg-slate-900/40 border-slate-800 opacity-60" : "bg-slate-900 border-slate-800")
                : (isHO ? "bg-slate-100 border-slate-200 opacity-60" : "bg-white border-slate-200")
                }`}
            >
              {/* DAY HEADER */}
              <div className={`p-5 border-b ${isDarkMode ? "border-slate-800 bg-black/20" : "border-slate-100 bg-slate-50"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                    {diasDeLaSemana[dateObj.getDay()]}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[8px] font-black uppercase tracking-widest ${isHO ? "text-emerald-500" : "text-slate-600"}`}>
                      {isHO ? '🏠 Home Office' : 'Retiro en sede'}
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isHO}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setHomeOffice(prev => ({ ...prev, [fecha]: isChecked }));
                          if (isChecked) setSelectedPlatos(prev => { const upd = { ...prev }; delete upd[fecha]; return upd; });
                        }}
                      />
                      <div className="w-9 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>
                <h3 className="text-lg font-black tracking-tight leading-none">
                  {dateObj.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                </h3>
                <div className="mt-1 flex items-center gap-1.5 font-black text-[9px] uppercase tracking-wider text-amber-500">
                  <Clock className="w-3 h-3" /> Turno Mañana
                </div>
              </div>

              {/* SEDE SELECTOR PER DAY */}
              {!isHO && (
                <div className={`px-5 py-3 border-b border-dashed ${isDarkMode ? "border-slate-800" : "border-slate-100"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-3 h-3 text-indigo-400" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">¿Dónde retiras tu vianda este día?</span>
                  </div>
                  <select
                    value={sedesPorDia[fecha] || userSedeId || ''}
                    onChange={(e) => setSedesPorDia(prev => ({ ...prev, [fecha]: Number(e.target.value) }))}
                    className={`w-full px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800 text-indigo-400" : "bg-slate-50 border-slate-200 text-indigo-600 focus:border-indigo-400"
                      } border`}
                  >
                    {sedes.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              )}

              {/* PLATO SELECTION */}
              <div className="p-5 space-y-6">
                {isHO ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-3 grayscale opacity-60 animate-in zoom-in duration-300">
                    <Home className="w-10 h-10 text-slate-500" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Retiro en casa</p>
                  </div>
                ) : (
                  tiposUnicos.map(tipo => (
                    platosOrganizados[fecha]?.[tipo]?.length > 0 && (
                      <div key={`${fecha}-${tipo}`} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
                          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-fuchsia-500/60">{tipo}</span>
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
                        </div>

                        <div className="space-y-2">
                          {(platosOrganizados[fecha][tipo] || [])
                            .filter(p => {
                              // Filtrar por sede
                              const sedeSeleccionadaDelDia = sedesPorDia[fecha] || userSedeId;
                              if (p.sede_id !== sedeSeleccionadaDelDia) return false;

                              // Si el usuario cambió manualmente la sede (no está usando su sede por defecto),
                              // mostrar TODOS los platos disponibles en esa sede
                              const esSuSedePorDefecto = String(sedeSeleccionadaDelDia) === String(userSedeId);
                              if (!esSuSedePorDefecto) {
                                return true; // Mostrar todo si eligió otra sede
                              }

                              // Solo aplicar restricción de tipos si está en su sede por defecto
                              const tiposHabilitados = tiposPorSede[sedeSeleccionadaDelDia] || [];

                              // Si no hay tipos configurados (array vacío), mostrar todos
                              if (tiposHabilitados.length === 0) return true;

                              // Verificar si el tipo del plato está habilitado
                              const platoTipo = (p.tipo || "").toUpperCase().trim();
                              return tiposHabilitados.some(th =>
                                platoTipo === th ||
                                platoTipo.includes(th) ||
                                th.includes(platoTipo)
                              );
                            })
                            .map(plato => {
                              const selected = !!isPlatoSelected(fecha, tipo, plato);
                              const platoId = plato.id_plato || plato.id;
                              const _rawCantidad = cantidades?.[fecha]?.[tipo]?.[platoId];
                              const cantidadValue = (!isNaN(_rawCantidad) && _rawCantidad >= 0) ? _rawCantidad : 0;
                              return (
                                <div key={plato.id_plato || plato.id} className="flex items-center gap-2">
                                  <label
                                    className={`flex-1 min-w-0 flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-all duration-300 ${selected
                                      ? (isDarkMode ? "bg-emerald-500/10 border-emerald-500/50 shadow-lg shadow-emerald-500/5 scale-[1.02]" : "bg-emerald-50 border-emerald-500 shadow-sm scale-[1.02]")
                                      : (isDarkMode ? "bg-slate-950/20 border-slate-800 hover:border-slate-700" : "bg-slate-50/50 border-slate-100 hover:border-slate-200")
                                      }`}
                                  >
                                    <div className={`mt-0.5 w-4 h-4 rounded-md border-2 flex items-center justify-center transition-all ${selected ? "bg-emerald-500 border-emerald-500" : "border-slate-700"}`}>
                                      {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                    </div>
                                    <input
                                      type="checkbox"
                                      className="hidden"
                                      checked={selected}
                                      onChange={() => handleCheckboxChange(fecha, tipo, plato)}
                                      disabled={hasExistingPedido}
                                    />
                                    <span className={`text-[11px] font-bold leading-tight transition-colors ${selected ? "text-emerald-400" : (isDarkMode ? "text-slate-400" : "text-slate-600")}`}>
                                      {plato.nombre}
                                    </span>
                                  </label>
                                  {/* Input de cantidad siempre visible para invitados/dirección */}
                                  {permiteMultiplesMenus && (
                                    <input
                                      type="number"
                                      min="0"
                                      className="shrink-0 w-14 px-1 py-2 rounded-lg border text-sm font-bold text-center bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                      style={{ minWidth: '3.5rem', fontSize: '0.95rem' }}
                                      value={cantidadValue}
                                      onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') return;
                                        handleCantidadChange(fecha, tipo, plato, raw);
                                      }}
                                      onBlur={e => handleCantidadChange(fecha, tipo, plato, e.target.value)}
                                      disabled={hasExistingPedido}
                                    />
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ModalConfirmacion
        show={showModalConfirmacion}
        handleClose={() => setShowModalConfirmacion(false)}
        handleConfirm={enviarSeleccionABaseDeDatos}
      />
    </div>
  );
};

export default Seleccion;
